import { Moment } from 'moment';
import groupBy from 'lodash/groupBy';
import upperFirst from 'lodash/upperFirst';

import { getI18nLocale, Locale, translate } from '@front/i18n';

import {
  FORMATS as I18N_FORMATS,
  DATE_FORMATS_BY_LOCALE,
  DateFormat,
  DateTimeFormat,
} from '@front/i18n/src/locales/date-formats';

import moment from './moment';
import { formatTime, parseTime } from './time';
import { formatStringListWithSeparator } from './formatting';

const t = translate('date');

// TODO I18N: Remove this and adapt imports
export const FORMATS = I18N_FORMATS;

const STANDARD_DATE = {
  format: 'YYYY-MM-DD',
  regex: /^\d{4}-\d{2}-\d{2}$/,
};

const getLocaleDateFormatting = () => DATE_FORMATS_BY_LOCALE[getI18nLocale()];

const getLocaleDefaultFormat = () => {
  const formats = getLocaleDateFormatting();
  return formats[FORMATS.DATE.DEFAULT];
};

/**
 * This function is used to check the format of the value returned by the date inputs we use in web.
 * At the time this comment is written, we cannot remember the exact reason why we put this logic in
 * place, but we suspect it has something to do with the OS (we believe it is because inputs of type
 * "date" in Android and iOS do not return the same format of value). At least, we know currently we
 * only receive these two formats : either 2020-01-31 or 31/01/2020.
 *
 * TODO : find out the exact reason.
 */
export const getDateFormat = (value: string) =>
  STANDARD_DATE.regex.test(value) ? STANDARD_DATE.format : getLocaleDefaultFormat();

export const formatDate = (
  value: Moment | string | undefined,
  format: DateFormat | DateTimeFormat = 'date_default',
  locale?: Locale, // TODO: !!! Beware, this typing is not correct, moment does not support all of our locales
) => {
  const formats = getLocaleDateFormatting();
  const momentDate = moment.isMoment(value) ? value : moment(value);
  const localizedMomentDate = locale ? momentDate.locale(locale) : momentDate;
  return localizedMomentDate.format(formats[format]);
};

export const parseDate = (value: string) => moment(value, getDateFormat(value), true);

export const parseValidDate = (value: string) => {
  const date = parseDate(value);
  return date.isValid() ? date : undefined;
};

export const formatStandardDate = (date: Moment | string) =>
  (moment.isMoment(date) ? date : moment(date)).format(STANDARD_DATE.format);

export const setTime = (date: Moment, timeValue?: string) => {
  const time = parseTime(timeValue);
  return date.clone().set({
    hour: time.hour(),
    minute: time.minute(),
    second: 0,
    millisecond: 0,
  });
};

export const getDatePlaceholder = () => t('.input.placeholder');

const getDurationDifference = (timestamp: Moment) => {
  const now = moment();

  const start = timestamp.isBefore(now) ? timestamp.clone() : now;
  const end = timestamp.isBefore(now) ? now : timestamp.clone();

  const years = end.diff(start, 'years');
  start.add(years, 'years');

  const months = end.diff(start, 'months');
  start.add(months, 'months');

  const days = end.diff(start, 'days');
  start.add(days, 'days');

  return { years, months, days };
};

export const formatDurationDifference = (timestamp?: string | Moment) => {
  const { years, months, days } = getDurationDifference(moment(timestamp));

  const formattedYears = t('.elapsed_duration.x_years', { count: years });
  const formattedMonths = t('.elapsed_duration.x_months', { count: months });
  const formattedDays = t('.elapsed_duration.x_days', { count: days });

  const timeArray = [
    ...(years > 0 ? [formattedYears] : []),
    ...(months > 0 ? [formattedMonths] : []),
    ...(days > 0 ? [formattedDays] : []),
  ];

  return formatStringListWithSeparator(timeArray) || formattedDays;
};

const getElapsedDuration = (timestamp: Moment, now: Moment) => {
  const diffInMinutes = Math.abs(now.diff(timestamp, 'minutes'));
  const diffInHours = Math.abs(now.diff(timestamp, 'hours'));
  const diffInDays = Math.abs(now.diff(timestamp, 'days'));

  if (diffInMinutes < 15) return t('.elapsed_duration.less_than_minute', { count: 15 });
  if (diffInMinutes < 30) return t('.elapsed_duration.x_minutes', { count: 15 });
  if (diffInMinutes < 45) return t('.elapsed_duration.x_minutes', { count: 30 });
  if (diffInMinutes < 60) return t('.elapsed_duration.x_minutes', { count: 45 });
  if (diffInHours < 24) return t('.elapsed_duration.x_hours', { count: Math.floor(diffInHours) });
  if (diffInDays < 31) return t('.elapsed_duration.x_days', { count: Math.floor(diffInDays) });
  return null;
};

export const formatElapsedDuration = (value: string | Moment) => {
  const now = moment();
  const timestamp = moment(value);
  const direction = timestamp.isBefore(now) ? 'past' : 'future';
  const time = getElapsedDuration(timestamp, now);

  if (time) {
    return t(`.elapsed_duration.${direction}`, { time });
  }
  return t('.elapsed_duration.date', { date: formatDate(timestamp, FORMATS.DATE.DAY_MONTH_YEAR) });
};

export const formatTimesByDate = (
  dates: (Moment | string)[],
  { separator = ', ', dayWithUpperFirst = true } = {},
) => {
  const datesGroupedByDate = groupBy(dates, formatStandardDate);

  return Object.keys(datesGroupedByDate).map((date) => {
    const [first, ...rest] = datesGroupedByDate[date];
    const formattedDate = formatDate(first, FORMATS.DATETIME.WEEKDAY_DAY_MONTH_AT_TIME_SHORT);
    const formattedDates = [
      dayWithUpperFirst ? upperFirst(formattedDate) : formattedDate,
      ...rest.map((d) => formatTime(d)),
    ];

    return formattedDates.join(separator);
  });
};

/*
 * To avoid timezone bugs when the user country cannot be defined (not logged in, no country in AsyncStorage),
 * we send datetimes with no timezone to the API.
 * The backend handle dates with no timezone very well, by applying the user account country timezone automatically.
 * See https://wecasa.atlassian.net/browse/BUG-845 and https://github.com/wecasa/front/pull/3759 for details.
 */
export const formateDateForAPI = (datetime: Moment) => formatDate(datetime, FORMATS.DATETIME.API_TIMESTAMP);
